import {Component, OnInit, Renderer2, AfterViewChecked, ElementRef, ViewChild, ViewChildren, OnDestroy, QueryList} from '@angular/core';
import {FormsModule} from "@angular/forms";
import * as feather from "feather-icons";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import * as bootstrap from "bootstrap";
import {GetGlobalConfigurationService} from "./services/get-global-configuration.service";
import {Subscription} from "rxjs";
import {DataTableDirective, DataTablesModule} from "angular-datatables";
import {NgClass, NgIf} from "@angular/common";
import {ListConfiguredVendorsService} from "./services/list-configured-vendors.service";
import {ListConfiguredVendorsDto} from "../auto-generated-server-dtos/adjuster/configuration/list-configured-vendors-dto";
import AjaxSettings = DataTables.AjaxSettings;
import {ListConfiguredRepairCentresService} from "./services/list-configured-repair-centres.service";
import {ListConfiguredRepairCentresDto} from "../auto-generated-server-dtos/adjuster/configuration/list-configured-repair-centres-dto";
import {SearchModalComponent} from "./components/search-modal/search-modal.component";
import {RefreshListConfiguredVendorsService} from "./services/refresh-list-configured-vendors.service";
import {RefreshListConfiguredRepairCentresService} from "./services/refresh-list-configured-repair-centres.service";
import {ConfirmGlobalConfigurationModalComponent} from "./components/confirm-global-configuration-modal/confirm-global-configuration-modal.component";
import {EditConfigurationModalComponent} from "./components/edit-configuration-modal/edit-configuration-modal.component";
import {DeleteConfigurationModalComponent} from "./components/delete-configuration-modal/delete-configuration-modal.component";

@Component({
    selector: 'app-configuration',
    standalone: true,
    imports: [
        FormsModule,
        DataTablesModule,
        NgClass,
    ],
    templateUrl: './configuration.component.html',
    styleUrl: './configuration.component.scss'
})
export class ConfigurationComponent implements OnInit, AfterViewChecked, OnDestroy {
    @ViewChild('vendorDataTable', { static: false }) vendorDataTableElement!: ElementRef;
    @ViewChild('repairCentreDataTable', { static: false }) repairCentreDataTableElement!: ElementRef;
    @ViewChildren(DataTableDirective) dataTables!: QueryList<DataTableDirective>;
    
    globalDollarLimit!: string;
    globalPercentageLimit!: string;
    isGlobalDollarLimitValid: boolean = true;
    isGlobalPercentageLimitValid: boolean = true;
    
    configuredVendors: ListConfiguredVendorsDto[] = [];
    configuredRepairCentres: ListConfiguredRepairCentresDto[] = [];
    refreshListConfiguredVendorsSubscription: Subscription;
    refreshListConfiguredRepairCentresSubscription: Subscription;

    dataTableOptions: DataTables.Settings = {};
    vendorDtOptions: DataTables.Settings = {};
    repairCentreDtOptions: DataTables.Settings = {};

    constructor(
        private getGlobalConfigurationService: GetGlobalConfigurationService,
        private listConfiguredVendorsService: ListConfiguredVendorsService,
        private listConfiguredRepairCentresService: ListConfiguredRepairCentresService,
        private refreshListConfiguredVendorsService: RefreshListConfiguredVendorsService,
        private refreshListConfiguredRepairCentresService: RefreshListConfiguredRepairCentresService,
        private renderer: Renderer2,
        private ngbModal: NgbModal,
    ) {
        this.refreshListConfiguredVendorsSubscription = this.refreshListConfiguredVendorsService.triggerVendorRefresh$.subscribe(() => {
            this.refreshDataTable('vendorDataTable');
        });
        this.refreshListConfiguredRepairCentresSubscription = this.refreshListConfiguredRepairCentresService.triggerRepairCentreRefresh$.subscribe(() => {
            this.refreshDataTable('repairCentreDataTable');
        });
    }

    ngOnInit(): void {
        this.getGlobalConfigurationService.getGlobalConfiguration().subscribe((response) => {
            this.globalDollarLimit = response.globalDollarLimit.toString();
            this.globalPercentageLimit = response.globalPercentageLimit?.toString() ?? '';
        });
        
        this.dataTableOptions = {
            dom: '<"row"<"col-md-6"i><"col-md-6"f>><"row"<"px-0"t>><"row"<"col-6 mt-2"l><"col-6"p>>',
            language: {
                info: 'Showing _START_ to _END_ of _TOTAL_ items',
                infoEmpty: 'Showing 0 items',
                infoFiltered: '<br>Filtered from _MAX_ total items',
                zeroRecords: 'No matching items found',
                lengthMenu: 'Show _MENU_ items',
                loadingRecords: '<div class="d-flex center py-1 w-100"><div class="spinner-border spinner-border-sm text-primary m-auto" role="status"><span class="sr-only">Loading...</span></div></div>',
            },
            lengthMenu: [
                [10, 25, 50, 100, -1],
                [10, 25, 50, 100, 'All']
            ],
            pageLength: 25,
            order: [[1, 'desc']],
            processing: true,
            stateSave: false,
            drawCallback: function () {
                feather.replace();
            }            
        };
        
        this.vendorDtOptions = {
            ...this.dataTableOptions,
            ...{
                columns: [{
                    title: 'ID',
                    data: 'vendorId'
                }, {
                    title: 'Name',
                    data: null,
                    className: 'w-auto',
                    render: (data) => {
                        return data.friendlyName ?? data.legalName;
                    }
                }, {
                    title: 'Address',
                    data: 'address',
                    className: 'w-auto',
                }, {
                    title: 'City',
                    data: 'city',
                    className: 'text-nowrap',
                }, {
                    title: 'Province/State',
                    data: 'province',
                }, {
                    title: '$ Amount',
                    data: 'maxPreApprovalAmount',
                    render: (data) => {
                        return data.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    }
                }, {
                    title: 'Percentage',
                    data: 'maxPreApprovalPercentage',
                }, {
                    title: '',
                    data: '',
                    searchable: false,
                    orderable: false,
                    className: 'text-end text-nowrap align-end py-1',
                    render: (data, type, row) => {
                        return `
                        <button class="btn btn-link p-0" ngbTooltip="Edit" data-action="edit" data-id="${row.vendorId}"><i data-feather="edit-2"></i></button>
                        <button class="btn btn-link p-0" ngbTooltip="Delete" data-action="delete" data-id="${row.vendorId}"><i class="text-danger" data-feather="trash"></i></button>
                    `;
                    }
                }],
                ajax: (ajaxSettings: AjaxSettings, callback) => {
                    this.listConfiguredVendorsService.listConfiguredVendors().subscribe((vendors) => {
                        this.configuredVendors = vendors;
                        callback({
                            data: vendors
                        });
                    });
                },
                rowCallback: (row: Node, data: any[] | Object, index: number) => {
                    let rowElement = row as HTMLElement;
                    
                    let editButton = rowElement.querySelector('[data-action="edit"]') as HTMLElement;
                    editButton?.removeAllListeners!('click');
                    editButton?.addEventListener('click', () => {
                        editButton.blur();
                        this.editVendorConfiguration(parseInt(editButton.dataset.id!));
                    });

                    let deleteButton = rowElement.querySelector('[data-action="delete"]') as HTMLElement;
                    deleteButton?.removeAllListeners!('click');
                    deleteButton?.addEventListener('click', () => {
                        deleteButton.blur();
                        this.deleteVendorConfiguration(parseInt(editButton.dataset.id!));
                    });                   
                    
                    return row;
                },
            }
        };

        this.repairCentreDtOptions = {
            ...this.dataTableOptions,
            ...{
                columns: [{
                    title: 'ID',
                    data: 'repairCentreId'
                }, {
                    title: 'Name',
                    data: 'name',
                    className: 'w-auto',
                }, {
                    title: 'Address',
                    data: 'address',
                    className: 'w-auto',
                }, {
                    title: 'City',
                    data: 'city',
                    className: 'text-nowrap',
                }, {
                    title: 'Province/State',
                    data: 'province',
                }, {
                    title: '$ Amount',
                    data: 'maxPreApprovalAmount',
                    render: (data) => {
                        return data.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    }
                }, {
                    title: 'Percentage',
                    data: 'maxPreApprovalPercentage',
                }, {
                    title: '',
                    data: '',
                    searchable: false,
                    orderable: false,
                    className: 'text-end text-nowrap align-end py-1',
                    render: (data, type, row) => {
                        return `
                        <button class="btn btn-link p-0" ngbTooltip="Edit" data-action="edit" data-id="${row.repairCentreId}"><i data-feather="edit-2"></i></button>
                        <button class="btn btn-link p-0" ngbTooltip="Delete" data-action="delete" data-id="${row.repairCentreId}"><i class="text-danger" data-feather="trash"></i></button>
                    `;
                    }
                }],
                ajax: (ajaxSettings: AjaxSettings, callback) => {
                    this.listConfiguredRepairCentresService.listConfiguredRepairCentres().subscribe((repairCentres) => {
                        this.configuredRepairCentres = repairCentres;
                        callback({
                            data: repairCentres
                        });
                    });
                },
                rowCallback: (row: Node, data: any[] | Object, index: number) => {
                    let rowElement = row as HTMLElement;
                    
                    let editButton = rowElement.querySelector('[data-action="edit"]') as HTMLElement;
                    editButton?.removeAllListeners!('click');
                    editButton?.addEventListener('click', () => {
                        editButton.blur();
                        this.editRepairCentreConfiguration(parseInt(editButton.dataset.id!));
                    });

                    let deleteButton = rowElement.querySelector('[data-action="delete"]') as HTMLElement;
                    deleteButton?.removeAllListeners!('click');
                    deleteButton?.addEventListener('click', () => {
                        deleteButton.blur();
                        this.deleteRepairCentreConfiguration(parseInt(editButton.dataset.id!));
                    });                    
                    return row;
                },
            }
        };
    }

    ngAfterViewChecked(): void {
        this.initializeTooltips();
    }

    validateGlobalAmountLimit(): void {
        this.isGlobalDollarLimitValid = /^\d+$/.test(this.globalDollarLimit) && parseInt(this.globalDollarLimit) >= 0;
    }
    
    validateGlobalPercentageLimit(): void {
        this.isGlobalPercentageLimitValid = /^\d+$/.test(this.globalPercentageLimit) && parseInt(this.globalPercentageLimit) >= 0;
    }

    initializeTooltips(): void {
        const vendorDtElements = this.vendorDataTableElement.nativeElement.querySelectorAll('[ngbTooltip]');
        const repairCenterDtElements = this.repairCentreDataTableElement.nativeElement.querySelectorAll('[ngbTooltip]');
        const elements = [...vendorDtElements, ...repairCenterDtElements];
        elements.forEach((element: HTMLElement) => {
            if (!element.hasAttribute('tooltip-initialized')) {
                const tooltipText = element.getAttribute('ngbTooltip');
                this.renderer.setAttribute(element, 'title', tooltipText!);
                new bootstrap.Tooltip(element, { offset: [0, 0]});
                this.renderer.setAttribute(element, 'tooltip-initialized', '');
            }
        });
    }
    
    searchVendor(): void {
        this.openSearchModal('vendor');
    }
    
    searchRepairCentre(): void {
        this.openSearchModal('repairCentre');   
    }
    
    openGlobalConfirmModal(): void {
        const modalRef = this.ngbModal.open(ConfirmGlobalConfigurationModalComponent);
        modalRef.componentInstance.maxAmountLimit = this.globalDollarLimit;
        modalRef.componentInstance.maxPercentageLimit = this.globalPercentageLimit;
    }

    openSearchModal(searchType: string): void {
        const modalRef = this.ngbModal.open(SearchModalComponent, {size: 'xl'});
        modalRef.componentInstance.searchType = searchType;
    }

    editVendorConfiguration(id: number): void {
        const selectedVendor = this.configuredVendors.find(vendor => vendor.vendorId === id);
        const modalRef = this.ngbModal.open(EditConfigurationModalComponent);
        modalRef.componentInstance.modalType = 'vendor';
        modalRef.componentInstance.vendor = selectedVendor;
    }

    editRepairCentreConfiguration(id: number): void {
        const selectedRepairCentre = this.configuredRepairCentres.find(repairCentre => repairCentre.repairCentreId === id);
        const modalRef = this.ngbModal.open(EditConfigurationModalComponent);
        modalRef.componentInstance.modalType = 'repairCentre';
        modalRef.componentInstance.repairCentre = selectedRepairCentre;
    }
    
    deleteVendorConfiguration(id: number): void {
        const selectedVendor = this.configuredVendors.find(vendor => vendor.vendorId === id);
        const modalRef = this.ngbModal.open(DeleteConfigurationModalComponent);
        modalRef.componentInstance.modalType = 'vendor';
        modalRef.componentInstance.vendor = selectedVendor;        
    }
    
    deleteRepairCentreConfiguration(id: number): void {
        const selectedRepairCentre = this.configuredRepairCentres.find(repairCentre => repairCentre.repairCentreId === id);
        const modalRef = this.ngbModal.open(DeleteConfigurationModalComponent);
        modalRef.componentInstance.modalType = 'repairCentre';
        modalRef.componentInstance.repairCentre = selectedRepairCentre;
    }

    refreshDataTable(dataTableId: string): void {
        const dtItems = this.dataTables.filter(dt => dt.dtInstance);
        dtItems.forEach(dtItem => {
            dtItem.dtInstance.then((dt: any) => {
                if (dt.table().node().id === dataTableId) {
                    dt.ajax.reload(null, false);
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.refreshListConfiguredVendorsSubscription.unsubscribe();
        this.refreshListConfiguredRepairCentresSubscription.unsubscribe();
    }
    
}
